import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Header, Footer } from "../components/layout";
import { AppDownloadBanner } from "../components/sections/AppDownloadBanner";
import { SEO } from "../components/SEO";
import { Container } from "../components/ui/Container";
import { getServices, BusinessItem } from "../apis/explorePages";
import { getSmartAppDownloadUrl } from "../utils/appLinks";
import {
  CITY_COORDINATES,
  POPULAR_LOCATIONS,
  DEFAULT_COORDINATES,
} from "../utils/locations";

export interface ServiceListingItem {
  id: string;
  title: string;
  category: string;
  providerName: string;
  providerSlug: string;
  price: number;
  rating: number | null;
  image: string;
  distance?: string | null;
  distanceKm?: number | null;
}

interface SidebarCategory {
  id: string;
  name: string;
  slug: string;
  subcategories?: string[];
  matcher: (item: ServiceListingItem) => boolean;
}

const SERVICE_SIDEBAR_CATEGORIES: SidebarCategory[] = [
  {
    id: "all",
    name: "All services",
    slug: "all",
    matcher: () => true,
  },
  {
    id: "salon",
    name: "Salon",
    slug: "salon",
    subcategories: ["Hair Cut & Styling", "Facial & Cleanup", "Spa & Massage", "Bridal Makeup", "Nail Art"],
    matcher: (item) =>
      /salon|beauty|hair|manicure|facial|spa|grooming/i.test(
        `${item.category} ${item.title}`
      ),
  },
  {
    id: "auto",
    name: "Auto",
    slug: "auto",
    subcategories: ["Car Wash", "Car Detailing", "Bike Service", "Auto Repair", "Oil Change"],
    matcher: (item) =>
      /auto|car|wash|detailing|tire|tyre|bike|vehicle|mechanic/i.test(
        `${item.category} ${item.title}`
      ),
  },
  {
    id: "cleaning",
    name: "Home Cleaning",
    slug: "home-cleaning",
    subcategories: ["Deep Cleaning", "Kitchen Cleaning", "Bathroom Cleaning", "Sofa & Carpet"],
    matcher: (item) =>
      /clean|housekeeping|maid|sanitiz/i.test(`${item.category} ${item.title}`),
  },
  {
    id: "repair",
    name: "Appliance & AC Repair",
    slug: "appliance-ac-repair",
    subcategories: ["AC Service & Repair", "Washing Machine", "Refrigerator", "Microwave"],
    matcher: (item) =>
      /repair|ac |air condition|appliance|fridge|washing machine/i.test(
        `${item.category} ${item.title}`
      ),
  },
  {
    id: "electrical",
    name: "Electrical & Plumbing",
    slug: "electrical-plumbing",
    subcategories: ["Electrician", "Plumber", "Wiring", "Pipe Fitting"],
    matcher: (item) =>
      /electr|plumb|wiring|pipe/i.test(`${item.category} ${item.title}`),
  },
  {
    id: "pest",
    name: "Pest Control",
    slug: "pest-control",
    subcategories: ["General Pest Control", "Termite Control", "Bed Bug Treatment", "Mosquito Control"],
    matcher: (item) =>
      /pest|termite|disinfect/i.test(`${item.category} ${item.title}`),
  },
];

export default function ServicesPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialCategory = searchParams.get("category") || "all";
  const initialSubcategory = searchParams.get("sub") || "";
  const initialSearch = searchParams.get("search") || "";
  const initialLocation = searchParams.get("location") || "All";

  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [selectedSubcategory, setSelectedSubcategory] = useState(initialSubcategory);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [selectedLocation, setSelectedLocation] = useState(initialLocation);
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(
    CITY_COORDINATES[initialLocation] || DEFAULT_COORDINATES
  );
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);

  const [serviceItems, setServiceItems] = useState<ServiceListingItem[]>([]);
  const [loading, setLoading] = useState(true);

  const locationDropdownRef = useRef<HTMLDivElement>(null);

  const toggleCategoryExpanded = (catId: string) => {
    setExpandedCategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) {
        next.delete(catId);
      } else {
        next.add(catId);
      }
      return next;
    });
  };

  // Auto-expand active category
  useEffect(() => {
    if (selectedCategory && selectedCategory !== "all") {
      const activeCat = SERVICE_SIDEBAR_CATEGORIES.find(
        (c) => c.id === selectedCategory || c.slug === selectedCategory
      );
      if (activeCat?.id && Array.isArray(activeCat.subcategories) && activeCat.subcategories.length > 0) {
        setExpandedCategoryIds((prev) => {
          const next = new Set(prev);
          next.add(activeCat.id);
          return next;
        });
      }
    }
  }, [selectedCategory]);

  // Background non-blocking GPS detection
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        if (selectedLocation === "All" || selectedLocation === "Current Location") {
          setUserCoords(coords);
        }
      },
      () => {},
      { maximumAge: 600_000, timeout: 10_000 }
    );
  }, []);

  const handleDetectGPS = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setSelectedLocation("Current Location");
        setUserCoords({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
        setIsLocationDropdownOpen(false);
      },
      () => {
        alert("Location access denied or unavailable.");
      }
    );
  };

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        locationDropdownRef.current &&
        !locationDropdownRef.current.contains(event.target as Node)
      ) {
        setIsLocationDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch services from live backend API
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const isAll =
      !selectedLocation ||
      selectedLocation === "All" ||
      selectedLocation.toLowerCase() === "all locations";

    getServices({
      location: isAll ? undefined : selectedLocation,
      lat: isAll ? undefined : userCoords?.lat,
      lng: isAll ? undefined : userCoords?.lng,
      sort: isAll ? "newest" : "nearest",
      limit: 500,
    })
      .then((res) => {
        if (!cancelled) {
          const rawItems: BusinessItem[] = res.items || [];
          // Map each service partner into service listings
          const mapped: ServiceListingItem[] = rawItems.map((b, idx) => {
            const rawCat = b.category || (b.serviceCategories && b.serviceCategories[0]) || "Service";
            let shortCat = "Service";
            if (/salon|beauty|hair|spa/i.test(rawCat)) shortCat = "Salon";
            else if (/auto|car|wash|mechanic/i.test(rawCat)) shortCat = "Auto";
            else if (/clean/i.test(rawCat)) shortCat = "Home Cleaning";
            else if (/repair/i.test(rawCat)) shortCat = "Repair";
            else if (/plumb|electr/i.test(rawCat)) shortCat = "Electrical";

            const rawRating = typeof b.rating === "number" && b.rating > 0 ? Number(b.rating.toFixed(1)) : null;

            return {
              id: b.id || b._id || `srv-${idx}`,
              title: b.featuredOffer || b.businessName || b.name,
              category: shortCat,
              providerName: b.businessName || b.name,
              providerSlug: b.slug || b.id || "",
              price: 350 + (idx % 5) * 150,
              rating: rawRating,
              image: b.coverImage || b.image || b.logo || "/deals/spotlight_home_service.jpg",
              distance: b.distance || null,
              distanceKm: b.distanceKm || null,
            };
          });

          setServiceItems(mapped);
        }
      })
      .catch((err) => {
        console.warn("getServices failed:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedLocation, userCoords]);

  // Sync to URL
  useEffect(() => {
    const params: Record<string, string> = {};
    if (selectedCategory && selectedCategory !== "all") {
      params.category = selectedCategory;
    }
    if (selectedSubcategory) {
      params.sub = selectedSubcategory;
    }
    if (searchQuery.trim()) {
      params.search = searchQuery.trim();
    }
    if (selectedLocation && selectedLocation !== "Ernakulam") {
      params.location = selectedLocation;
    }
    setSearchParams(params, { replace: true });
  }, [selectedCategory, selectedSubcategory, searchQuery, selectedLocation, setSearchParams]);

  // Compute category counts
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: serviceItems.length };
    SERVICE_SIDEBAR_CATEGORIES.forEach((cat) => {
      if (cat.id !== "all") {
        counts[cat.id] = serviceItems.filter(cat.matcher).length;
      }
    });
    return counts;
  }, [serviceItems]);

  const matchServiceToSubcategory = (item: ServiceListingItem, subName: string): boolean => {
    const normSub = subName.toLowerCase().trim();
    const title = (item.title || "").toLowerCase();
    const cat = (item.category || "").toLowerCase();
    return title.includes(normSub) || cat.includes(normSub);
  };

  // Compute subcategory counts
  const subcategoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    SERVICE_SIDEBAR_CATEGORIES.forEach((cat) => {
      if (Array.isArray(cat.subcategories)) {
        cat.subcategories.forEach((sub) => {
          counts[`${cat.id}:${sub}`] = serviceItems.filter((item) =>
            matchServiceToSubcategory(item, sub)
          ).length;
        });
      }
    });
    return counts;
  }, [serviceItems]);

  // Filtered services
  const filteredServices = useMemo(() => {
    let list = [...serviceItems];

    if (selectedSubcategory && selectedSubcategory.trim()) {
      list = list.filter((s) => matchServiceToSubcategory(s, selectedSubcategory));
    } else {
      const activeCat = SERVICE_SIDEBAR_CATEGORIES.find(
        (c) => c.id === selectedCategory || c.slug === selectedCategory
      );
      if (activeCat && activeCat.id !== "all") {
        list = list.filter(activeCat.matcher);
      }
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (s) =>
          s.title.toLowerCase().includes(q) ||
          s.providerName.toLowerCase().includes(q) ||
          s.category.toLowerCase().includes(q)
      );
    }

    // Sort by distance (nearest first)
    list.sort((a, b) => {
      const da = a.distanceKm != null ? a.distanceKm : Infinity;
      const db = b.distanceKm != null ? b.distanceKm : Infinity;
      return da - db;
    });

    return list;
  }, [serviceItems, selectedCategory, searchQuery]);

  const activeCategoryObj =
    SERVICE_SIDEBAR_CATEGORIES.find(
      (c) => c.id === selectedCategory || c.slug === selectedCategory
    ) || SERVICE_SIDEBAR_CATEGORIES[0];

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-800 flex flex-col font-sans selection:bg-purple-100 selection:text-purple-900">
      <SEO
        title={
          selectedLocation === "All"
            ? "Explore Services across Kerala | SetGo"
            : selectedLocation === "Current Location"
            ? "Services Near You | SetGo"
            : `Explore services near ${selectedLocation} | SetGo`
        }
        description={`Book verified local service experts for haircut, salon, car wash, home cleaning, electrical repair ${
          selectedLocation === "All"
            ? "across Kerala"
            : `near ${selectedLocation}`
        }.`}
        url="/services"
      />

      <Header variant="solid" />

      <main className="flex-1 pt-24 pb-16">
        <Container size="xl" padding="md">
          {/* ── BREADCRUMB ───────────────────────────────────────────────────────── */}
          <nav aria-label="Breadcrumb" className="mb-4">
            <ol className="flex items-center gap-2 text-xs sm:text-sm text-slate-500">
              <li>
                <Link to="/deals" className="hover:text-slate-900 transition-colors">
                  Deals near you
                </Link>
              </li>
              <li className="text-slate-400">/</li>
              <li className="font-bold text-slate-900">Explore services</li>
            </ol>
          </nav>

          {/* ── HEADER ROW: TITLE & AREA SELECTOR ─────────────────────────────────── */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-950 tracking-tight leading-tight">
                {selectedLocation === "All"
                  ? "Explore services"
                  : selectedLocation === "Current Location"
                  ? "Services near you"
                  : `Services near ${selectedLocation}`}
              </h1>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <p className="text-xs sm:text-sm text-slate-500">
                  {filteredServices.length}{" "}
                  {filteredServices.length === 1 ? "service" : "services"} ·{" "}
                  {selectedSubcategory
                    ? `${selectedSubcategory} (${activeCategoryObj.name})`
                    : activeCategoryObj.id === "all"
                    ? "Salon & Auto"
                    : activeCategoryObj.name}
                </p>
                {selectedSubcategory && (
                  <button
                    type="button"
                    onClick={() => setSelectedSubcategory("")}
                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-purple-100 hover:bg-purple-200 text-[#552FB5] text-xs font-semibold transition-colors"
                    title="Remove subcategory filter"
                  >
                    <span>{selectedSubcategory}</span>
                    <span className="text-[10px]">✕</span>
                  </button>
                )}
              </div>
            </div>

            {/* Area Selector Dropdown */}
            <div className="relative self-start sm:self-auto" ref={locationDropdownRef}>
              <div className="text-[11px] font-medium text-slate-400 mb-1">
                Browsing area
              </div>
              <button
                type="button"
                onClick={() => setIsLocationDropdownOpen(!isLocationDropdownOpen)}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-xs sm:text-sm font-semibold text-slate-900 hover:border-slate-300 shadow-xs transition-colors"
              >
                <span>{selectedLocation === "All" ? "All Locations" : selectedLocation}</span>
                <svg
                  className={`w-4 h-4 text-slate-500 transition-transform ${
                    isLocationDropdownOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isLocationDropdownOpen && (
                <div className="absolute right-0 mt-1 w-60 bg-white rounded-2xl shadow-xl border border-slate-100 py-1.5 z-40 max-h-72 overflow-y-auto">
                  {/* GPS Option */}
                  <button
                    type="button"
                    onClick={handleDetectGPS}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs font-bold text-purple-700 bg-purple-50/70 hover:bg-purple-100 transition-colors border-b border-slate-100 text-left"
                  >
                    <svg className="w-4 h-4 flex-shrink-0 animate-pulse text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" strokeWidth="2" />
                      <circle cx="12" cy="12" r="4" fill="currentColor" />
                    </svg>
                    <span>Use Current GPS Location</span>
                  </button>

                  {/* "All" Option */}
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedLocation("All");
                      setIsLocationDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-xs sm:text-sm flex items-center justify-between transition-colors border-b border-slate-100 ${
                      selectedLocation === "All"
                        ? "bg-purple-50 text-[#552FB5] font-bold"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <span>All Locations (Distance sorted)</span>
                    {selectedLocation === "All" && (
                      <svg className="w-4 h-4 text-[#552FB5]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>

                  <div className="px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Districts (Kerala)
                  </div>

                  {POPULAR_LOCATIONS.map((loc) => (
                    <button
                      key={loc}
                      type="button"
                      onClick={() => {
                        setSelectedLocation(loc);
                        if (CITY_COORDINATES[loc]) {
                          setUserCoords(CITY_COORDINATES[loc]);
                        }
                        setIsLocationDropdownOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-xs sm:text-sm flex items-center justify-between transition-colors ${
                        selectedLocation === loc
                          ? "bg-purple-50 text-[#552FB5] font-bold"
                          : "text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <span>{loc}</span>
                      {selectedLocation === loc && (
                        <svg className="w-4 h-4 text-[#552FB5]" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── 2-COLUMN LAYOUT: SIDEBAR + SERVICES GRID ───────────────────────────── */}
          <div className="flex flex-col lg:flex-row gap-8 items-start">
            {/* ── LEFT SIDEBAR (Sticky on Desktop) ─────────────────────────────────── */}
            <aside className="w-full lg:w-72 flex-shrink-0 lg:sticky lg:top-24 lg:self-start lg:h-fit z-10">
              <div className="glass-light rounded-3xl p-4 sm:p-5 space-y-4 lg:max-h-[calc(100vh-7rem)] flex flex-col">
                {/* Search Input */}
                <div className="relative flex-shrink-0">
                  <svg
                    className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search services"
                    className="w-full bg-[#F1F3F9] border-0 rounded-xl pl-9 pr-3.5 py-2.5 text-xs sm:text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-[#552FB5] transition-all"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Categories Header */}
                <div className="flex-1 min-h-0 flex flex-col">
                  <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2 flex-shrink-0">
                    CATEGORY
                  </div>

                  <div className="max-h-64 lg:max-h-none flex-1 min-h-0 overflow-y-auto pr-1 space-y-1 scrollbar-thin scrollbar-thumb-purple-200 hover:scrollbar-thumb-purple-300 scrollbar-track-transparent">
                    {SERVICE_SIDEBAR_CATEGORIES.map((cat) => {
                      const isActive =
                        selectedCategory === cat.id || selectedCategory === cat.slug;
                      const count = categoryCounts[cat.id] ?? 0;
                      const hasSubcategories = Array.isArray(cat.subcategories) && cat.subcategories.length > 0;
                      const isExpanded = expandedCategoryIds.has(cat.id);

                      return (
                        <div key={cat.id} className="space-y-0.5">
                          <div
                            className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs sm:text-sm transition-all text-left ${
                              isActive && !selectedSubcategory
                                ? "bg-[#EDE9FE] text-[#552FB5] font-bold shadow-xs"
                                : isActive
                                ? "bg-purple-50/70 text-[#552FB5] font-semibold"
                                : "text-slate-600 hover:bg-slate-50 font-medium"
                            }`}
                          >
                            {/* Main Category Click */}
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedCategory(cat.id);
                                setSelectedSubcategory("");
                                if (hasSubcategories && !isExpanded) {
                                  toggleCategoryExpanded(cat.id);
                                }
                              }}
                              className="flex-1 min-w-0 text-left truncate pr-1"
                            >
                              <span className="truncate">{cat.name}</span>
                            </button>

                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <span
                                className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                  isActive && !selectedSubcategory
                                    ? "bg-[#552FB5] text-white"
                                    : count > 0
                                    ? "bg-purple-100 text-[#552FB5]"
                                    : "text-slate-400"
                                }`}
                              >
                                {count}
                              </span>

                              {/* Expand / Collapse Chevron Button */}
                              {hasSubcategories && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCategoryExpanded(cat.id);
                                  }}
                                  className="p-1 rounded-lg hover:bg-purple-100/70 text-slate-400 hover:text-slate-700 transition-colors"
                                  aria-label={isExpanded ? `Collapse ${cat.name}` : `Expand ${cat.name}`}
                                >
                                  <svg
                                    className={`w-3.5 h-3.5 transition-transform duration-200 ${
                                      isExpanded ? "rotate-180 text-[#552FB5]" : ""
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Subcategories Dropdown Tree */}
                          {hasSubcategories && isExpanded && (
                            <div className="border-l-2 border-purple-200/80 ml-4 pl-2 space-y-0.5 py-1">
                              {/* "All [Category]" Option */}
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedCategory(cat.id);
                                  setSelectedSubcategory("");
                                }}
                                className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs transition-colors text-left ${
                                  isActive && !selectedSubcategory
                                    ? "bg-[#552FB5] text-white font-bold"
                                    : "text-slate-500 hover:text-slate-900 hover:bg-slate-100/60 font-medium"
                                }`}
                              >
                                <span className="truncate">All in {cat.name}</span>
                                <span
                                  className={`text-[10px] px-1.5 py-0.2 rounded-full font-semibold ${
                                    isActive && !selectedSubcategory
                                      ? "bg-white/20 text-white"
                                      : "text-slate-400"
                                  }`}
                                >
                                  {count}
                                </span>
                              </button>

                              {/* Individual Subcategories */}
                              {cat.subcategories!.map((sub) => {
                                const isSubActive = isActive && selectedSubcategory === sub;
                                const subCount = subcategoryCounts[`${cat.id}:${sub}`] ?? 0;

                                return (
                                  <button
                                    key={sub}
                                    type="button"
                                    onClick={() => {
                                      setSelectedCategory(cat.id);
                                      setSelectedSubcategory(sub);
                                    }}
                                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs transition-all text-left ${
                                      isSubActive
                                        ? "bg-[#552FB5] text-white font-bold shadow-xs"
                                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-100/70 font-normal"
                                    }`}
                                  >
                                    <div className="flex items-center gap-1.5 min-w-0 pr-1">
                                      <span
                                        className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                          isSubActive ? "bg-white" : "bg-purple-300"
                                        }`}
                                      />
                                      <span className="truncate">{sub}</span>
                                    </div>
                                    {subCount > 0 && (
                                      <span
                                        className={`text-[10px] font-semibold px-1.5 py-0.2 rounded-full flex-shrink-0 ${
                                          isSubActive
                                            ? "bg-white/20 text-white"
                                            : "bg-purple-50 text-purple-700"
                                        }`}
                                      >
                                        {subCount}
                                      </span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Promo Card: "Take SetGo with you" */}
                <div className="hidden lg:block rounded-2xl p-4 bg-[#F8F9FE] border border-purple-100/80 flex-shrink-0">
                  <h4 className="font-extrabold text-xs sm:text-sm text-slate-900 mb-1">
                    Take SetGo with you
                  </h4>
                  <p className="text-[11px] text-slate-500 leading-relaxed mb-3">
                    Book services, redeem offers and earn coins in the SetGo app.
                  </p>
                  <a
                    href={getSmartAppDownloadUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full py-2 rounded-xl bg-[#552FB5] hover:bg-[#432096] text-white text-center font-bold text-xs shadow-xs transition-colors"
                  >
                    Get the app
                  </a>
                </div>
              </div>
            </aside>

            {/* ── RIGHT SERVICES GRID ─────────────────────────────────────────────── */}
            <section className="flex-1 min-w-0 w-full">
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <div
                      key={n}
                      className="h-72 rounded-2xl bg-slate-200/60 animate-pulse border border-slate-100"
                    />
                  ))}
                </div>
              ) : filteredServices.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-3xl border border-dashed border-slate-200 p-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-50 text-[#552FB5] flex items-center justify-center text-2xl font-bold">
                    🔧
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1">
                    No services found {selectedLocation !== "All" ? `near ${selectedLocation}` : ""}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-500 mb-5 max-w-md mx-auto">
                    {selectedLocation !== "All"
                      ? `We couldn't find any services in ${selectedLocation} yet. Try exploring all available services across Kerala sorted by distance.`
                      : `We couldn't find any services matching "${searchQuery || activeCategoryObj.name}". Try selecting another category or clearing your search.`}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedLocation("All");
                      setSelectedCategory("all");
                      setSearchQuery("");
                    }}
                    className="px-5 py-2.5 rounded-full bg-[#552FB5] text-white text-xs font-bold hover:bg-[#432096] transition-colors shadow-xs inline-flex items-center gap-1.5"
                  >
                    <span>View All Services (Distance sorted)</span>
                    <span>→</span>
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {filteredServices.map((srv) => (
                      <div
                        key={srv.id}
                        className="group bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-xs hover:shadow-md transition-all duration-200 flex flex-col justify-between"
                      >
                        {/* Image with Category Badge on Top-Left */}
                        <div className="relative aspect-[16/10] w-full overflow-hidden bg-slate-100">
                          <img
                            src={srv.image}
                            alt={srv.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute top-2.5 left-2.5">
                            <span className="inline-block px-2.5 py-0.5 rounded-lg text-[10px] sm:text-[11px] font-bold bg-white/95 backdrop-blur-sm text-[#552FB5] border border-purple-100 shadow-xs">
                              {srv.category}
                            </span>
                          </div>
                        </div>

                        {/* Card Body */}
                        <div className="p-4 flex flex-col justify-between flex-1">
                          <div>
                            <h3 className="font-extrabold text-sm sm:text-base text-slate-900 group-hover:text-[#552FB5] transition-colors truncate">
                              {srv.title}
                            </h3>
                            <p className="text-xs text-slate-500 mt-0.5 mb-3 truncate">
                              by{" "}
                              <Link
                                to={`/shops/${srv.providerSlug}`}
                                className="font-medium text-slate-700 hover:text-[#552FB5] transition-colors"
                              >
                                {srv.providerName}
                              </Link>
                            </p>

                            {/* Price & Rating Row */}
                            <div className="flex items-center justify-between pt-2 border-t border-slate-50">
                              <span className="font-black text-sm sm:text-base text-slate-900">
                                ₹ {srv.price}
                              </span>

                              {srv.rating && (
                                <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-800">
                                  <span>{srv.rating.toFixed(1)}</span>
                                  <span className="text-amber-500">★</span>
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Book in the app link */}
                          <div className="mt-3 pt-2">
                            <a
                              href={getSmartAppDownloadUrl()}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs font-bold text-[#552FB5] group-hover:underline inline-flex items-center gap-1"
                            >
                              Book in the app →
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Summary Count below grid */}
                  <div className="mt-8 text-center">
                    <p className="text-xs text-slate-400">
                      Showing all {filteredServices.length} services
                    </p>
                  </div>
                </>
              )}
            </section>
          </div>
        </Container>
      </main>

      {/* ── APP DOWNLOAD BANNER & FOOTER ─────────────────────────────────────────── */}
      <AppDownloadBanner />
      <Footer />
    </div>
  );
}
